/**
 * Photographs of a car.
 *
 * The files live in a private storage bucket; this module records what is
 * there and hands back object URLs the browser can show. Private means every
 * image is fetched as the signed-in user rather than linked publicly, so the
 * URLs here are short-lived and local to the page.
 */

import { supabase } from "../../lib/supabase";
import { makeZip } from "../../lib/zip";
import { nothingChanged } from "../../lib/saving";

export const BUCKET = "vehicle-photos";

/** Above this, a phone photo is resized before upload. Six or eight shots of
    every car at full camera resolution would fill the free storage tier in a
    couple of months, and nothing on this screen needs more. */
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.82;

export interface VehiclePhoto {
  /** Off for pictures of paperwork, which live in the same gallery. */
  show_on_website?: boolean;
  id: string;
  vehicle_id: string;
  storage_path: string;
  caption: string | null;
  sort_order: number;
  byte_size: number | null;
  width: number | null;
  height: number | null;
  created_at: string;
}

export class PhotoError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = "PhotoError";
  }
}

/* ------------------------------------------------------------------ */
/* Getting a picture down to a sensible size                           */
/* ------------------------------------------------------------------ */

/**
 * Resize on the phone before uploading.
 *
 * A modern phone camera produces 3–5 MB per shot. Eight of those per car,
 * across a few hundred cars, is more storage than the whole rest of the
 * system will ever use. The long edge is capped and the result re-encoded as
 * JPEG, which lands around 200–400 KB with no visible loss at the size these
 * are ever displayed.
 */
async function shrink(file: File): Promise<{ blob: Blob; width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new PhotoError("This browser could not process the image.");
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
  if (!blob) throw new PhotoError("This browser could not process the image.");
  return { blob, width, height };
}

/* ------------------------------------------------------------------ */
/* Reading and writing                                                 */
/* ------------------------------------------------------------------ */

export async function listPhotos(vehicleId: string): Promise<VehiclePhoto[]> {
  const { data, error } = await supabase
    .from("vehicle_photos")
    .select("*")
    .eq("vehicle_id", vehicleId)
    .order("sort_order")
    .order("created_at");
  if (error) throw new PhotoError(`Could not read the photos: ${error.message}`);
  return (data ?? []) as VehiclePhoto[];
}

export async function uploadPhoto(
  vehicleId: string,
  file: File,
  nextOrder: number,
): Promise<VehiclePhoto> {
  if (!file.type.startsWith("image/")) {
    throw new PhotoError("That is not an image.");
  }

  const { blob, width, height } = await shrink(file);
  const path = `${vehicleId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;

  const up = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, { contentType: "image/jpeg", upsert: false });
  if (up.error) {
    const msg = up.error.message;
    if (/bucket/i.test(msg) && /not found|does not exist/i.test(msg)) {
      throw new PhotoError(
        "The vehicle-photos bucket does not exist yet. Run 14_vehicle_photos.sql "
        + "in Supabase.", up.error);
    }
    throw new PhotoError(`Could not upload: ${msg}`, up.error);
  }

  const { data, error } = await supabase
    .from("vehicle_photos")
    .insert({
      vehicle_id: vehicleId, storage_path: path,
      sort_order: nextOrder, byte_size: blob.size, width, height,
    })
    .select("*")
    .single();

  if (error || !data) {
    // An object with nothing pointing at it is invisible and counts against
    // storage forever, so it does not get to stay.
    await supabase.storage.from(BUCKET).remove([path]);
    throw new PhotoError(
      `The photo uploaded but could not be filed: ${error?.message ?? "no row returned"}`,
      error);
  }
  return data as VehiclePhoto;
}

/**
 * Remove every photo of a car.
 *
 * One storage call and one delete rather than a loop: twenty round trips is
 * slow on a phone and can leave the job half finished if the connection drops
 * partway. Files first, as always — a row removed before its file leaves an
 * object nothing points at, counting against storage forever.
 */
export async function deleteAllPhotos(photos: VehiclePhoto[]) {
  if (!photos.length) return;
  const { error: storageError } = await supabase.storage
    .from(BUCKET).remove(photos.map((p) => p.storage_path));
  if (storageError) {
    throw new PhotoError(`Could not remove the files: ${storageError.message}`);
  }
  const { error } = await supabase
    .from("vehicle_photos").delete().in("id", photos.map((p) => p.id));
  if (error) throw new PhotoError(`Could not remove the photos: ${error.message}`);
}

/** File first, then row. The other order leaves an orphan behind. */
export async function deletePhoto(photo: VehiclePhoto) {
  const { error: storageError } = await supabase.storage
    .from(BUCKET).remove([photo.storage_path]);
  if (storageError) {
    throw new PhotoError(`Could not remove the file: ${storageError.message}`);
  }
  const { error } = await supabase.from("vehicle_photos").delete().eq("id", photo.id);
  if (error) throw new PhotoError(`Could not remove the photo: ${error.message}`);
}

export async function setCaption(id: string, caption: string) {
  const { error } = await supabase.from("vehicle_photos")
    .update({ caption: caption.trim() || null }).eq("id", id);
  if (error) throw new PhotoError(`Could not save the caption: ${error.message}`);
}

/**
 * Write the running order back.
 *
 * Every row is renumbered from zero rather than only the two that moved, so
 * the numbering stays contiguous no matter how a photo got where it is. With
 * twenty photos that is twenty small updates, which is cheap and leaves no
 * room for two photos to end up claiming the same position.
 */
export async function saveOrder(photos: VehiclePhoto[]) {
  const results = await Promise.all(
    photos.map((p, i) =>
      supabase.from("vehicle_photos").update({ sort_order: i }).eq("id", p.id).select("id")));
  const failed = results.find((r) => r.error);
  if (failed?.error) {
    throw new PhotoError(`Could not save the new order: ${failed.error.message}`);
  }
  if (results.some((r) => !r.data?.length)) throw nothingChanged();
}

/** Move one photo to a new position, returning the rearranged list. */
export function moveTo(photos: VehiclePhoto[], id: string, to: number): VehiclePhoto[] {
  const from = photos.findIndex((p) => p.id === id);
  if (from < 0 || to < 0 || to >= photos.length || from === to) return photos;
  const next = [...photos];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next.map((p, i) => ({ ...p, sort_order: i }));
}

/* ------------------------------------------------------------------ */
/* Showing them                                                        */
/* ------------------------------------------------------------------ */

/**
 * Fetch a photo's bytes and hand back a URL the page can use.
 *
 * The caller owns the URL and must revoke it when the component unmounts,
 * otherwise every visit to a vehicle leaks a few megabytes of blob.
 */
/**
 * A small version of a photo, for lists.
 *
 * A cover shot is 200–400 KB. Thirty of them on the vehicles screen is ten
 * megabytes over whatever connection the phone happens to be on, to show
 * pictures the size of a postage stamp. Storage resizes on its way out
 * instead, which brings each one under about 15 KB.
 *
 * Transformations are a paid feature, so a failure here falls back to the
 * full image rather than showing nothing.
 */
export async function thumbUrl(path: string, width = 160): Promise<string> {
  const t = await supabase.storage.from(BUCKET).download(path, {
    transform: { width, height: Math.round(width * 0.75), resize: "cover" },
  });
  if (!t.error && t.data) return URL.createObjectURL(t.data);
  return photoUrl(path);
}

/** Cover shots for a set of vehicles, in one query. */
export async function coverPaths(
  vehicleIds: string[],
): Promise<Record<string, string>> {
  if (!vehicleIds.length) return {};
  const { data, error } = await supabase
    .from("vehicle_cover_photo")
    .select("vehicle_id,storage_path")
    .in("vehicle_id", vehicleIds);
  if (error || !data) return {};   // a list without pictures still works
  return Object.fromEntries(
    data.map((r) => [r.vehicle_id as string, r.storage_path as string]));
}

export async function photoUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET).download(path);
  if (error || !data) {
    throw new PhotoError(`Could not load the photo: ${error?.message ?? "not found"}`);
  }
  return URL.createObjectURL(data);
}

/* ------------------------------------------------------------------ */
/* Taking them away                                                    */
/* ------------------------------------------------------------------ */

/**
 * Every photo of a car, as one zip.
 *
 * Numbered in running order and named after the stock number, so the files
 * still make sense once they are out of the archive and sitting in somebody's
 * downloads folder — 146451-01.jpg, not a row of UUIDs.
 *
 * Downloads run a few at a time. All at once floods a phone connection and
 * Supabase starts refusing them; one at a time is needlessly slow with twenty
 * photos.
 */
export async function zipAllPhotos(
  photos: VehiclePhoto[],
  stockNo: string,
  onProgress?: (done: number, total: number) => void,
): Promise<Blob> {
  if (!photos.length) throw new PhotoError("There are no photos to download.");

  const BATCH = 4;
  const entries: { name: string; bytes: Uint8Array<ArrayBuffer>; modified: Date }[] = [];
  let done = 0;

  for (let i = 0; i < photos.length; i += BATCH) {
    const slice = photos.slice(i, i + BATCH);
    const got = await Promise.all(slice.map(async (p, k) => {
      const { data, error } = await supabase.storage.from(BUCKET).download(p.storage_path);
      if (error || !data) {
        throw new PhotoError(
          `Could not fetch photo ${i + k + 1}: ${error?.message ?? "not found"}`);
      }
      return {
        name: `${stockNo}-${String(i + k + 1).padStart(2, "0")}.jpg`,
        bytes: new Uint8Array(await data.arrayBuffer()) as Uint8Array<ArrayBuffer>,
        modified: new Date(p.created_at),
      };
    }));
    entries.push(...got);
    done += got.length;
    onProgress?.(done, photos.length);
  }

  return makeZip(entries);
}

/** Hand a blob to the browser as a download. */
/**
 * A filename for the download: "2012 BMW X3 720110".
 *
 * Slashes, colons and quotes are stripped because macOS and Windows disagree
 * about which of them are legal in a filename, and a download that silently
 * fails to save is worse than an ugly name.
 */
export function zipName(vehicleName: string, stockNo: string): string {
  const clean = `${vehicleName} ${stockNo}`
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return clean || stockNo;
}

export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/* ------------------------------------------------------------------ */
/* For the listing draft                                               */
/* ------------------------------------------------------------------ */

/**
 * A few photos, small, as base64 — what gets sent to be described.
 *
 * Shrunk hard on purpose. A model reads a 700px photo of a car as well as a
 * 3000px one, and the smaller the image the less it costs and the faster it
 * goes over a phone connection.
 */
export async function photosForDescription(
  photos: VehiclePhoto[],
  count = 4,
): Promise<string[]> {
  const out: string[] = [];
  for (const p of photos.slice(0, count)) {
    const { data } = await supabase.storage.from(BUCKET).download(p.storage_path, {
      transform: { width: 700, resize: "contain" },
    });
    const blob = data ?? (await supabase.storage.from(BUCKET).download(p.storage_path)).data;
    if (!blob) continue;
    out.push(await toBase64(blob));
  }
  if (!out.length) throw new PhotoError("Could not read the photos.");
  return out;
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    // The data URL prefix is not part of the base64 the API wants.
    r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = () => reject(new PhotoError("Could not read a photo."));
    r.readAsDataURL(blob);
  });
}

/** Publish a photo on the website, or keep it out of it. */
export async function setPhotoPublic(id: string, show: boolean) {
  const { data, error } = await supabase
    .from("vehicle_photos").update({ show_on_website: show }).eq("id", id).select("id");
  if (error) throw new PhotoError(`Could not change it: ${error.message}`);
  if (!data?.length) throw nothingChanged();
}
